import dayjs from "dayjs";
import { baseOptions } from "../../../../DashboardPage/components/chartsOptions";
import { formatCount } from "../../../utils/formatNumber";
import { formatRate } from "../../../VAMP/utils/formatNumber";

const dateFormat = "MMM D, YYYY";

const RIGHT_AXIS_SERIES = ["three_ds", "three_ds_previous"];

const hasRightAxisSeries = (visibleSeries) =>
  !visibleSeries || RIGHT_AXIS_SERIES.some((id) => visibleSeries.has(id));

const hasLeftAxisSeries = (visibleSeries) =>
  !visibleSeries || [...visibleSeries].some((id) => !RIGHT_AXIS_SERIES.includes(id));

const getRightAxisMax = (visibleSeries) => (hasLeftAxisSeries(visibleSeries) ? 100 : undefined);

export const getChartOptions = (theme, setTooltip, dataType, visibleSeries) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  scales: {
    x: {
      ...baseOptions(theme).scales.x,
      ticks: {
        color: "#7A7A83",
        font: { size: 12, weight: 400 },
      },
    },
    y: {
      type: "linear",
      position: "left",
      display: hasLeftAxisSeries(visibleSeries),
      beginAtZero: true,
      grid: {
        color: theme === "dark" ? "rgba(255, 255, 255, 0.12)" : "#E1E1E2",
        tickColor: "transparent",
      },
      border: { display: false },
      ticks: {
        maxTicksLimit: 6,
        font: { size: 12, weight: 400 },
        color: "#7A7A83",
        callback: (value) =>
          dataType === "rate" ? formatRate(value) : formatCount(value),
      },
    },
    y1: {
      type: "linear",
      position: "right",
      display: hasRightAxisSeries(visibleSeries),
      beginAtZero: true,
      max: dataType === "rate" ? getRightAxisMax(visibleSeries) : undefined,
      grid: {
        drawOnChartArea: !hasLeftAxisSeries(visibleSeries),
        color: theme === "dark" ? "rgba(255, 255, 255, 0.12)" : "#E1E1E2",
        tickColor: "transparent",
      },
      border: { display: false },
      ticks: {
        maxTicksLimit: 6,
        font: { size: 12, weight: 400 },
        color: "#7A7A83",
        callback: (value) =>
          dataType === "rate" ? formatRate(value) : formatCount(value),
      },
    },
  },
  plugins: {
    "vertical-line-plugin-with-points": { theme: theme },
    legend: { display: false },
    tooltip: {
      enabled: false,
      external: function (context) {
        const tooltipModel = context.tooltip;
        if (tooltipModel.opacity === 0) {
          setTooltip({ visible: false });
          if (context.chart.canvas._hideOnTouchOutside) {
            document.removeEventListener(
              "touchstart",
              context.chart.canvas._hideOnTouchOutside
            );
            context.chart.canvas._hideOnTouchOutside = null;
          }
          return;
        }

        const position = context.chart.canvas.getBoundingClientRect();
        const dataPoint = context.tooltip.dataPoints?.[0];
        const dataIndex = dataPoint?.dataIndex;

        const mainDataset = context.chart.data.datasets.find((d) =>
          Array.isArray(d.mainLabels)
        );
        const rawMainDate = mainDataset?.mainLabels?.[dataIndex] ?? dataPoint?.label;

        const currentPoints = context.chart.data.datasets.map((dataset) => {
          const rawCompDate = dataset.comparisonLabels?.[dataIndex];
          return {
            value: dataset.data[dataIndex],
            datasetType: dataset.datasetType,
            backgroundColor: dataset.borderColor,
            label: dataset.label,
            dashed:
              Array.isArray(dataset.borderDash) && dataset.borderDash.length > 0,
            comparisonDate: rawCompDate
              ? dayjs(rawCompDate).format(dateFormat)
              : null,
          };
        });

        const payload = {
          visible: true,
          x: position.left + window.pageXOffset + tooltipModel.caretX,
          y: position.top + window.pageYOffset + tooltipModel.caretY,
          date: rawMainDate ? dayjs(rawMainDate).format(dateFormat) : null,
          data: currentPoints,
        };
        setTooltip(payload);

        if (!context.chart.canvas._hideOnTouchOutside) {
          const canvas = context.chart.canvas;
          canvas._hideOnTouchOutside = (e) => {
            if (!canvas.contains(e.target)) {
              setTooltip({ visible: false });
              document.removeEventListener(
                "touchstart",
                canvas._hideOnTouchOutside
              );
              canvas._hideOnTouchOutside = null;
            }
          };
          document.addEventListener("touchstart", canvas._hideOnTouchOutside, {
            passive: true,
          });
        }
      },
    },
  },
});

export const datasetsOptions = {
  chargeback: {
    borderColor: "#225AEA",
    backgroundColor: "rgba(34, 90, 234, 0.08)",
    borderWidth: 2,
    datasetType: "chargeback",
    yAxisID: "y",
  },
  fraud: {
    borderColor: "#DF7009",
    backgroundColor: "rgba(223, 112, 9, 0.08)",
    borderWidth: 2,
    datasetType: "fraud",
    yAxisID: "y",
  },
  three_ds: {
    borderColor: "#35BD29",
    borderWidth: 2,
    datasetType: "three_ds",
    yAxisID: "y1",
    fill: false,
  },
  ecm_zone: {
    borderColor: "#8C6FF7",
    borderDash: [2, 3],
    borderWidth: 1.5,
    datasetType: "ecm_zone",
    yAxisID: "y",
  },
  hecm_zone: {
    borderColor: "#4A4A4A",
    borderDash: [2, 3],
    borderWidth: 1.5,
    datasetType: "hecm_zone",
    yAxisID: "y",
  },
  efm_zone: {
    borderColor: "#D71313",
    borderDash: [2, 3],
    borderWidth: 1.5,
    datasetType: "efm_zone",
    yAxisID: "y",
  },
};
